import React, { type JSX } from "react";

import { findDuplicates } from "@impero-com/common/utils/array";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    DataExtractor,
    DataExtractorGroup,
    ExtractorExpander,
    PartialData,
} from "@impero-com/common/utils/dataView";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Space } from "antd";
import { flow } from "lodash-es";
import { Link } from "react-router-dom";

import {
    UserId,
    ReportId,
    UserOverview,
    StandardAccessRight,
} from "api/generated/model";
import { AccessRightTags } from "components/accessRightTags";
import { DeleteResourceLink } from "components/deleteResourceLink";
import { IdAndName } from "org/pageState/models";
import {
    ALL_ACCESS_RIGHTS,
    accessRightCategoryExtractorId,
    formatAccessRight,
} from "utils/accessRights";
import { downgradeExtractorType } from "utils/dataView";

import { LegacyRoute, LegacyRouteFragment } from "../../../legacyRoutes";
import { WebRoute } from "../../../routes";
import { useDeleteSavedReportMutation } from "../queries";
import type {
    Classification,
    DataSheetReport,
    Report,
    ReportIdentifier,
    ReportType,
    SavedReport,
} from "../types";

import { ReportListPageType } from "./layout";
import { buildReportUrl } from "./urlUtils";

const REPORT_COLUMN_IDS = {
    NAME: "name",
    // Used only on /reports/all, where Heterogeneous reports needs to be handled.
    NAME_FOR_ALL: "nameForAll",
    REPORT_TYPE: "reportType",
    DESCRIPTION: "description",
    PUSH_REPORTS_COUNT: "pushReportsCount",
    ACTION: "action",
    CLASSIFICATION: "Classification",
    DATASHEET: "Datasheet",
    CONTROL_PROGRAM: "controlProgram",
    DATASHEET_ARCHIVED: "datasheetArchived",
    ACCESS_RIGHTS: "accessRights",
} as const;

type ReportColumnId =
    (typeof REPORT_COLUMN_IDS)[keyof typeof REPORT_COLUMN_IDS];

interface ExtractorsAndExpanders {
    expanders: Array<ExtractorExpander<unknown, unknown>>;
    extractorGroups: Array<DataExtractorGroup<Report>>;
}

function buildExtractorsAndExpanders(
    pageType: ReportListPageType,
    columnIds: Array<ReportColumnId>,
    reports: Array<Report>
): ExtractorsAndExpanders {
    // Sanity check for duplicate columnIds.
    const duplicatedIds = findDuplicates(columnIds);
    if (duplicatedIds.length > 0) {
        throw new Error(
            `Duplicate columnIds found: ${duplicatedIds.join(", ")}`
        );
    }

    const context = buildExtractorBuilderContext(pageType, reports);
    const standardExtractors = columnIds.map((columnId) =>
        buildExtractor(columnId, context)
    );

    const extractorGroups = [
        {
            id: "standard",
            label: "Standard",
            extractors: standardExtractors,
        },
    ];
    const expanders: Array<ExtractorExpander<unknown, unknown>> = [];

    // In case if Access Rights column requested - add extractors for each access right category (Admin, Edit, View),
    // which are "hidden" columns that can be searched by.
    if (columnIds.includes(REPORT_COLUMN_IDS.ACCESS_RIGHTS)) {
        const {
            extractors: accessRightExtractors,
            expanders: accessRightExpanders,
        } = buildExtractorsAndExpandersForAccessRights(context);
        accessRightExtractors.forEach((extractor) => {
            standardExtractors.push(downgradeExtractorType(extractor));
        });
        accessRightExpanders.forEach((expander) => {
            expanders.push(expander as ExtractorExpander<unknown, unknown>);
        });
    }

    return {
        extractorGroups,
        expanders,
    };
}

function buildExtractor(
    columnId: ReportColumnId,
    context: ExtractorBuilderContext
): DataExtractor<Report, unknown> {
    switch (columnId) {
        case REPORT_COLUMN_IDS.NAME:
            return downgradeExtractorType(buildNameExtractor(context));
        case REPORT_COLUMN_IDS.NAME_FOR_ALL:
            return downgradeExtractorType(buildNameForAllExtractor(context));
        case REPORT_COLUMN_IDS.REPORT_TYPE:
            return downgradeExtractorType(buildReportTypeExtractor());
        case REPORT_COLUMN_IDS.DESCRIPTION:
            return downgradeExtractorType(buildDescriptionExtractor());
        case REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT:
            return downgradeExtractorType(buildPushReportsCountExtractor());
        case REPORT_COLUMN_IDS.ACTION:
            return downgradeExtractorType(buildActionExtractor());
        case REPORT_COLUMN_IDS.CLASSIFICATION:
            return downgradeExtractorType(
                buildClassificationExtractor(context)
            );
        case REPORT_COLUMN_IDS.DATASHEET:
            return downgradeExtractorType(buildDatasheetExtractor(context));
        case REPORT_COLUMN_IDS.CONTROL_PROGRAM:
            return downgradeExtractorType(
                buildControlProgramExtractor(context)
            );
        case REPORT_COLUMN_IDS.DATASHEET_ARCHIVED:
            return downgradeExtractorType(buildDatasheetArchivedExtractor());
        case REPORT_COLUMN_IDS.ACCESS_RIGHTS:
            return downgradeExtractorType(buildAccessRightsExtractor());
        default:
            return assertNever(columnId);
    }
}

function buildNameForAllExtractor(
    context: ExtractorBuilderContext
): DataExtractor<Report, string, IdAndName<ReportId, string>> {
    const getReportIdAndName = (
        report: Report
    ): IdAndName<ReportId, string> => {
        return {
            id: {
                type: report.type,
                data: getReportId(report),
            },
            name: getReportName(report),
        };
    };

    const lookupReportName = (
        idAndName: IdAndName<ReportId, string>
    ): string => {
        const { id, name } = idAndName;
        const identifier = buildReportIdentifier(id.type, id.data);
        // Try to get a report name by Id (in case if the report was renamed
        // after filter was created). If not found, that use the name that was originally saved in the filter.
        return context.reportIdentifierToNameMap.get(identifier) ?? name;
    };

    return {
        id: REPORT_COLUMN_IDS.NAME_FOR_ALL,
        label: t("Report_List_Column_Name"),
        getValue: getReportName,
        compare: Cmp.map(getReportName, Cmp.string()),
        asString: getReportName,
        customFilterTransformer: {
            getValue: getReportIdAndName,
            asString: lookupReportName,
        },
    };
}

function buildNameExtractor(
    context: ExtractorBuilderContext
): DataExtractor<Report, string, IdAndName<string, string>> {
    const getReportIdAndName = (report: Report): IdAndName<string, string> => {
        return {
            id: getReportId(report),
            name: getReportName(report),
        };
    };

    const lookupReportName = (idAndName: IdAndName<string, string>): string => {
        const pageTypeToReportType = (
            pageType: ReportListPageType
        ): ReportType => {
            switch (pageType) {
                case "Classification":
                case "Status":
                case "Workload":
                    return pageType;
                case "Datasheet":
                case "All":
                    throw new Error(
                        `buildNameExtractor() should not be used for '${context.pageType}' page type`
                    );
                default:
                    return assertNever(pageType);
            }
        };
        const reportType = pageTypeToReportType(context.pageType);
        const identifier = buildReportIdentifier(reportType, idAndName.id);
        return (
            context.reportIdentifierToNameMap.get(identifier) ?? idAndName.name
        );
    };

    return {
        id: REPORT_COLUMN_IDS.NAME,
        label: t("Report_List_Column_Name"),
        getValue: getReportName,
        compare: Cmp.map(getReportName, Cmp.string()),
        asString: getReportName,
        customFilterTransformer: {
            getValue: getReportIdAndName,
            asString: lookupReportName,
        },
    };
}

function getReportName(report: Report): string {
    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification": {
            return report.data.title;
        }
        case "Datasheet": {
            return report.data.dataSheetName;
        }
        default: {
            return assertNever(report);
        }
    }
}

interface NameCellProps {
    report: Report;
    pageType: ReportListPageType;
}

function NameCell({ report, pageType }: NameCellProps): JSX.Element {
    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification":
            return (
                <a
                    href={buildReportUrl(
                        `${LegacyRoute.REPORT_CONTROL_RESULT_STATUS_SAVED}/${report.data.id}`,
                        pageType
                    )}
                >
                    {report.data.title}
                </a>
            );
        case "Datasheet":
            return (
                <DatasheetReportLink
                    id={report.data.dataSheetId}
                    name={report.data.dataSheetName}
                    pageType={pageType}
                />
            );
        default: {
            return assertNever(report);
        }
    }
}

function buildReportTypeExtractor(): DataExtractor<Report, ReportType> {
    const extractReportType = (report: Report): ReportType => report.type;
    const extractAndLocalizeReportType = (report: Report): string =>
        localizeReportType(extractReportType(report));

    return {
        id: REPORT_COLUMN_IDS.REPORT_TYPE,
        label: t("Report_List_Column_Type"),
        getValue: extractReportType,
        compare: Cmp.map(extractAndLocalizeReportType, Cmp.string()),
        asString: extractAndLocalizeReportType,
        customFilterTransformer: {
            getValue: extractReportType,
            asString: localizeReportType,
        },
    };
}

function localizeReportType(reportType: ReportType): string {
    switch (reportType) {
        case "Status":
            return t("Report_Type_Status");
        case "Workload":
            return t("Report_Type_Workload");
        case "Classification":
            return t("Report_Type_Classification");
        case "Datasheet":
            return t("Report_Type_Datasheet");
        default:
            return assertNever(reportType);
    }
}

function buildDescriptionExtractor(): DataExtractor<Report, string> {
    const extractDescription = (report: Report): string => {
        switch (report.type) {
            case "Status":
            case "Workload":
            case "Classification": {
                return report.data.description ?? "";
            }
            case "Datasheet": {
                return "";
            }
            default: {
                return assertNever(report);
            }
        }
    };

    return {
        id: REPORT_COLUMN_IDS.DESCRIPTION,
        label: t("Report_List_Column_Description"),
        getValue: extractDescription,
        compare: Cmp.map(extractDescription, Cmp.string()),
        asString: extractDescription,
    };
}

function buildPushReportsCountExtractor(): DataExtractor<
    Report,
    number | null
> {
    return {
        id: REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
        label: t("Report_List_Column_PushReports"),
        getValue: getPushReportsCount,
        compare: Cmp.map(
            getPushReportsCount,
            Cmp.undefinedFirst(Cmp.primitive)
        ),
        asString: getPushReportsCountAsString,
        customFilterTransformer: {
            getValue: getPushReportsCount,
            asString: pushReportsCountToString,
        },
    };
}

function getPushReportsCountAsString(report: Report): string {
    const count = getPushReportsCount(report);
    return pushReportsCountToString(count);
}

function pushReportsCountToString(count: number | null): string {
    return count !== null ? count.toString() : "";
}

function getPushReportsCount(report: Report): number | null {
    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification":
            return getPushReportsCountForSavedReport(report.data);
        case "Datasheet":
            return null;
        default:
            return assertNever(report);
    }
}

function getPushReportsCountForSavedReport(savedReport: SavedReport): number {
    return savedReport.pushReportConfigurationCount;
}

function getReportId(report: Report): string {
    // Note: SavedReport.id as well as DataSheetReport.dataSheetId are UUIDs,
    // so we don't need to worry about a potential clash.
    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification": {
            return report.data.id;
        }
        case "Datasheet": {
            return report.data.dataSheetId;
        }
        default: {
            return assertNever(report);
        }
    }
}

interface PushReportsCountCellProps {
    report: Report;
}

function PushReportsCountCell({
    report,
}: PushReportsCountCellProps): JSX.Element {
    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification": {
            const count = getPushReportsCountForSavedReport(report.data);
            if (count > 0) {
                const savedReportId = getReportId(report);
                const pushReportsPath = `${LegacyRoute.REPORT_CONTROL_RESULT_STATUS_SAVED}/${savedReportId}/${LegacyRouteFragment.CONFIGURATIONS}`;
                return <a href={pushReportsPath}>{count}</a>;
            } else {
                return <>0</>;
            }
        }
        case "Datasheet":
            return <></>;
        default:
            return assertNever(report);
    }
}

function buildActionExtractor(): DataExtractor<Report, null> {
    return {
        id: REPORT_COLUMN_IDS.ACTION,
        label: t("Report_List_Column_Action"),
        getValue: () => null,
        compare: Cmp.map(() => null, Cmp.primitive),
        asString: () => "",
    };
}

interface ActionCellProps {
    report: Report;
}

function ActionCell({ report }: ActionCellProps): JSX.Element {
    let maybeAccessRightsLink: React.ReactNode | null = null;
    let maybeDeleteLink: React.ReactNode | null = null;

    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification": {
            if (report.data.actions.admin) {
                const url = `${WebRoute.REPORTS}/${report.data.id}/access-rights`;
                maybeAccessRightsLink = (
                    <Link to={url}>{t("Report_List_Action_AccessRights")}</Link>
                );
                maybeDeleteLink = (
                    <DeleteSavedReportLink savedReport={report.data} />
                );
            }
            break;
        }
        case "Datasheet": {
            break;
        }
        default: {
            return assertNever(report);
        }
    }

    return (
        <Space>
            {maybeAccessRightsLink}
            {maybeDeleteLink}
        </Space>
    );
}

interface DeleteSavedReportLinkProps {
    savedReport: SavedReport;
}

function DeleteSavedReportLink({
    savedReport,
}: DeleteSavedReportLinkProps): JSX.Element {
    const { mutate, isPending } = useDeleteSavedReportMutation();
    return (
        <DeleteResourceLink
            okButton={t("Report_Action_Delete_Confirm")}
            cancelButton={t("Report_Action_Delete_Cancel")}
            confirmationTitle={t("Report_SavedReports_Dialog_Title")}
            confirmationBody={t("Report_SavedReports_Dialog_Body", {
                name: savedReport.title,
            })}
            deleteResource={() => mutate({ id: savedReport.id })}
            isPending={isPending}
        >
            {t("Report_List_Action_Delete")}
        </DeleteResourceLink>
    );
}

/**
 * This extractor is used on the classification reports page.
 * If it's used against a report which is not of type "Classification", an error will be thrown.
 */
function buildClassificationExtractor(
    context: ExtractorBuilderContext
): DataExtractor<Report, string, IdAndName<string, string>> {
    const getClassification = (report: Report): Classification => {
        if (report.type !== "Classification") {
            throw new Error(
                "buildClassificationExtractor() should be used only for classification reports"
            );
        }
        return report.data.classification;
    };
    const getClassificationTitle = (report: Report): string =>
        getClassification(report).title;
    const getClassificationIdAndName = (
        report: Report
    ): IdAndName<string, string> => {
        const classification = getClassification(report);
        return { id: classification.id, name: classification.title };
    };
    const lookupClassificationName = (
        idAndName: IdAndName<string, string>
    ): string => {
        // Try to get a classification name by Id (in case if the classification name was renamed
        // after filter was created). If not found, that use the name that was originally saved in the filter.
        return (
            context.classificationIdToNameMap.get(idAndName.id) ??
            idAndName.name
        );
    };

    return {
        id: REPORT_COLUMN_IDS.CLASSIFICATION,
        label: t("Report_List_Column_Classification"),
        getValue: getClassificationTitle,
        compare: Cmp.map(getClassificationTitle, Cmp.string()),
        asString: getClassificationTitle,
        customFilterTransformer: {
            getValue: getClassificationIdAndName,
            asString: lookupClassificationName,
        },
    };
}

/**
 * This extractor is used on the datasheet reports page.
 * If it's used against a report which is not of type "Datasheet", an error will be thrown.
 */
function buildDatasheetExtractor(
    context: ExtractorBuilderContext
): DataExtractor<Report, string, IdAndName<string, string>> {
    const getDatasheetReport = (report: Report): DataSheetReport => {
        if (report.type !== "Datasheet") {
            throw new Error(
                "buildDatasheetExtractor() should be used only for datasheet reports"
            );
        }
        return report.data;
    };

    const getDatasheetName = (report: Report): string =>
        getDatasheetReport(report).dataSheetName;

    const getDatasheetIdAndName = (
        report: Report
    ): IdAndName<string, string> => {
        const datasheet = getDatasheetReport(report);
        return { id: datasheet.dataSheetId, name: datasheet.dataSheetName };
    };

    const lookupDatasheetName = (
        idAndName: IdAndName<string, string>
    ): string => {
        return context.datasheetIdToNameMap.get(idAndName.id) ?? idAndName.name;
    };

    return {
        id: REPORT_COLUMN_IDS.DATASHEET,
        label: t("Report_List_Column_Datasheet"),
        getValue: getDatasheetName,
        compare: Cmp.map(getDatasheetName, Cmp.string()),
        asString: getDatasheetName,
        customFilterTransformer: {
            getValue: getDatasheetIdAndName,
            asString: lookupDatasheetName,
        },
    };
}

interface DatasheetCellProps {
    report: Report;
    pageType: ReportListPageType;
}

function DatasheetCell({ report, pageType }: DatasheetCellProps): JSX.Element {
    if (report.type !== "Datasheet") {
        throw new Error(
            "DatasheetCell should be used only for datasheet reports"
        );
    }
    return (
        <DatasheetReportLink
            id={report.data.dataSheetId}
            name={report.data.dataSheetName}
            pageType={pageType}
        />
    );
}

interface DatasheetReportLinkProps {
    id: string;
    name: string;
    pageType: ReportListPageType;
}

function DatasheetReportLink({
    id,
    name,
    pageType,
}: DatasheetReportLinkProps): JSX.Element {
    return (
        <a
            href={buildReportUrl(
                `${LegacyRoute.REPORT_DATA_SHEET}/`,
                pageType,
                {
                    dataSheetId: id,
                }
            )}
        >
            {name}
        </a>
    );
}

/**
 * Suitable for datasheet reports only.
 */
function buildControlProgramExtractor(
    context: ExtractorBuilderContext
): DataExtractor<Report, string, IdAndName<number, string>> {
    const getDataSheetReport = (report: Report): DataSheetReport => {
        if (report.type !== "Datasheet") {
            throw new Error(
                "buildControlProgramExtractor() should be used only for datasheet reports"
            );
        }
        return report.data;
    };

    const getControlProgramName = (report: Report): string =>
        getDataSheetReport(report).controlProgramName;

    const getControlProgramIdAndName = (
        report: Report
    ): IdAndName<number, string> => {
        const datasheet = getDataSheetReport(report);
        return {
            id: datasheet.controlProgramId,
            name: datasheet.controlProgramName,
        };
    };

    const lookupControlProgramName = (
        idAndName: IdAndName<number, string>
    ): string => {
        // Try to get a control program name by Id (in case if the control program name was renamed
        // since filter was created). If not found, that use the name that was originally saved in the filter.
        return (
            context.controlProgramIdToNameMap.get(idAndName.id) ??
            idAndName.name
        );
    };

    return {
        id: REPORT_COLUMN_IDS.CONTROL_PROGRAM,
        label: t("Report_List_Column_ControlProgram"),
        getValue: getControlProgramName,
        compare: Cmp.map(getControlProgramName, Cmp.string()),
        asString: getControlProgramName,
        customFilterTransformer: {
            getValue: getControlProgramIdAndName,
            asString: lookupControlProgramName,
        },
    };
}

/**
 * Suitable for datasheet reports only.
 */
function buildDatasheetArchivedExtractor(): DataExtractor<Report, boolean> {
    const getArchived = (report: Report): boolean => {
        if (report.type !== "Datasheet") {
            throw new Error(
                "buildDatasheetArchivedExtractor() should be used only for datasheet reports"
            );
        }
        return report.data.archived;
    };
    const localizeArchived = (archived: boolean): string =>
        archived
            ? t("Report_List_Column_DatasheetArchived_Deleted")
            : t("Report_List_Column_DatasheetArchived_Active");
    const getArhivedLocalized = flow(getArchived, localizeArchived);

    return {
        id: REPORT_COLUMN_IDS.DATASHEET_ARCHIVED,
        label: t("Report_List_Column_DatasheetArchived"),
        getValue: getArchived,
        compare: Cmp.map(getArhivedLocalized, Cmp.string()),
        asString: getArhivedLocalized,
        customFilterTransformer: {
            getValue: getArchived,
            asString: localizeArchived,
        },
    };
}

function getUsersByAccessRight(
    report: Report
): Map<StandardAccessRight, Array<UserOverview>> | null {
    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification": {
            return report.data.usersByAccessRight;
        }
        case "Datasheet": {
            return null;
        }
        default: {
            return assertNever(report);
        }
    }
}

function buildAccessRightsExtractor(): DataExtractor<
    Report,
    Map<StandardAccessRight, Array<UserOverview>> | null
> {
    const countUsersWithAccess = (report: Report): number => {
        const usersByAccessRight = getUsersByAccessRight(report);
        if (usersByAccessRight === null) {
            return 0;
        }
        return Array.from(usersByAccessRight.values()).reduce(
            (sum, users) => sum + users.length,
            0
        );
    };

    return {
        id: REPORT_COLUMN_IDS.ACCESS_RIGHTS,
        label: t("AccessRights_AccessRightsColumn"),
        getValue: getUsersByAccessRight,
        compare: Cmp.map(countUsersWithAccess, Cmp.primitive),
        asString: (_report) => "",
    };
}

interface AccessRightsCellProps {
    report: Report;
}

function AccessRightsCell({ report }: AccessRightsCellProps): JSX.Element {
    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification": {
            return (
                <AccessRightTags
                    usersByAccessRight={report.data.usersByAccessRight}
                />
            );
        }
        case "Datasheet": {
            return <></>;
        }
        default: {
            return assertNever(report);
        }
    }
}

interface ExtractorsAndExpandersForAccessRights {
    extractors: Array<
        DataExtractor<
            Report,
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<number, string>
        >
    >;
    expanders: Array<
        ExtractorExpander<
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<number, string>
        >
    >;
}

function buildExtractorsAndExpandersForAccessRights(
    context: ExtractorBuilderContext
): ExtractorsAndExpandersForAccessRights {
    const extractors: Array<
        DataExtractor<
            Report,
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<UserId, string>
        >
    > = [];
    const expanders: Array<
        ExtractorExpander<
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<UserId, string>
        >
    > = [];

    const lookupUserName = (
        userIdAndName: IdAndName<UserId, string>
    ): string => {
        const { id, name } = userIdAndName;
        return context.userIdToNameMap.get(id) ?? name;
    };

    ALL_ACCESS_RIGHTS.forEach((accessRight: StandardAccessRight) => {
        const getUsers = (report: Report): Array<UserOverview> => {
            const usersByAccessRight = getUsersByAccessRight(report);
            if (usersByAccessRight === null) {
                return [];
            }
            return usersByAccessRight.get(accessRight) ?? [];
        };

        const extractorId = accessRightCategoryExtractorId(accessRight);

        const extractor: DataExtractor<
            Report,
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<UserId, string>
        > = {
            id: extractorId,
            label: formatAccessRight(accessRight),
            getValue: (report) => {
                return getUsersByAccessRight(report) ?? new Map();
            },
            compare: Cmp.map(
                getUsers,
                Cmp.map((extrs) => extrs.map(formatUserName).join(","))
            ),
            asString: (report) =>
                getUsers(report).map(formatUserName).join(", "),
            customFilterTransformer: {
                getValue: (_row) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${extractorId} extractor.`
                    );
                },
                asString: lookupUserName,
            },
        };
        extractors.push(extractor);

        const expander: ExtractorExpander<
            Map<StandardAccessRight, UserOverview[]>,
            IdAndName<UserId, string>
        > = {
            extractorId,
            expand: (
                usersByAccessRight: Map<StandardAccessRight, UserOverview[]>
            ) => {
                const results: Array<PartialData<IdAndName<UserId, string>>> =
                    [];

                const users: Array<UserOverview> =
                    usersByAccessRight.get(accessRight) || [];
                users.forEach((user: UserOverview) => {
                    const userIdAndName: IdAndName<UserId, string> = {
                        id: user.id,
                        name: formatUserName(user),
                    };
                    results.push({
                        partialId: `${accessRightCategoryExtractorId(
                            accessRight
                        )}-${user.id}`,
                        expandedValue: userIdAndName,
                    });
                });
                return results;
            },
            compare: Cmp.map(lookupUserName, Cmp.string()),
            asString: lookupUserName,
            customFilterTransformer: {
                getValue: (userIdAndName) => userIdAndName,
                asString: lookupUserName,
            },
        };

        expanders.push(expander);
    });

    return {
        extractors,
        expanders,
    };
}

/*
 * Extra data needed to build extractors.
 */
interface ExtractorBuilderContext {
    pageType: ReportListPageType;
    controlProgramIdToNameMap: Map<number, string>;
    datasheetIdToNameMap: Map<string, string>;
    classificationIdToNameMap: Map<string, string>;
    reportIdentifierToNameMap: Map<ReportIdentifier, string>;
    userIdToNameMap: Map<UserId, string>;
}

function buildExtractorBuilderContext(
    pageType: ReportListPageType,
    reports: Array<Report>
): ExtractorBuilderContext {
    return {
        pageType,
        controlProgramIdToNameMap: buildControlProgramIdToNameMap(reports),
        datasheetIdToNameMap: buildDatasheetIdToNameMap(reports),
        classificationIdToNameMap: buildClassificationIdToNameMap(reports),
        reportIdentifierToNameMap: buildReportIdentifierToNameMap(reports),
        userIdToNameMap: buildUserIdToNameMap(reports),
    };
}

function buildControlProgramIdToNameMap(
    reports: Array<Report>
): Map<number, string> {
    const cpIdToNameMap: Map<number, string> = new Map();
    reports.forEach((report) => {
        switch (report.type) {
            case "Datasheet": {
                cpIdToNameMap.set(
                    report.data.controlProgramId,
                    report.data.controlProgramName
                );
                break;
            }
            case "Status":
            case "Workload":
            case "Classification": {
                break;
            }
            default: {
                assertNever(report);
            }
        }
    });
    return cpIdToNameMap;
}

function buildDatasheetIdToNameMap(
    reports: Array<Report>
): Map<string, string> {
    const datasheetIdToNameMap: Map<string, string> = new Map();
    reports.forEach((report) => {
        switch (report.type) {
            case "Datasheet": {
                datasheetIdToNameMap.set(
                    report.data.dataSheetId,
                    report.data.dataSheetName
                );
                break;
            }
            case "Status":
            case "Workload":
            case "Classification": {
                break;
            }
            default: {
                assertNever(report);
            }
        }
    });
    return datasheetIdToNameMap;
}

function buildClassificationIdToNameMap(
    reports: Array<Report>
): Map<string, string> {
    const classificationIdToNameMap: Map<string, string> = new Map();
    reports.forEach((report) => {
        switch (report.type) {
            case "Classification": {
                classificationIdToNameMap.set(
                    report.data.classification.id,
                    report.data.classification.title
                );
                break;
            }
            case "Status":
            case "Workload":
            case "Datasheet": {
                break;
            }
            default: {
                assertNever(report);
            }
        }
    });
    return classificationIdToNameMap;
}

function buildUserIdToNameMap(reports: Array<Report>): Map<number, string> {
    const userIdToNameMap: Map<number, string> = new Map();
    reports.forEach((report) => {
        switch (report.type) {
            case "Status":
            case "Workload":
            case "Classification": {
                Array.from(report.data.usersByAccessRight.values())
                    .flat()
                    .forEach((user) => {
                        userIdToNameMap.set(user.id, formatUserName(user));
                    });
                break;
            }
            case "Datasheet": {
                break;
            }
            default: {
                assertNever(report);
            }
        }
    });
    return userIdToNameMap;
}

function buildReportIdentifierToNameMap(
    reports: Array<Report>
): Map<ReportIdentifier, string> {
    const map = new Map<ReportIdentifier, string>();
    reports.forEach((report) => {
        const identifier = buildReportIdentifier(
            report.type,
            getReportId(report)
        );
        const name = getReportName(report);
        map.set(identifier, name);
    });
    return map;
}

function buildReportIdentifier(
    reportType: ReportType,
    id: string
): ReportIdentifier {
    return `${reportType}:${id}`;
}

export {
    REPORT_COLUMN_IDS,
    buildExtractorsAndExpanders,
    getReportName,
    NameCell,
    PushReportsCountCell,
    ActionCell,
    DatasheetCell,
    AccessRightsCell,
};

export type { ReportColumnId };
